import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BlockWithAlignableContents } from '@lexical/react/LexicalBlockWithAlignableContents';
import { withDeleteButton } from '../withDeleteButton';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNodeByKey, ElementFormatType, NodeKey } from 'lexical';
import { $isTweetNode } from './TweetNode';

const WIDGET_SCRIPT_URL = 'https://platform.twitter.com/widgets.js';
let isTwitterScriptLoading = true;

type TweetComponentProps = Readonly<{
  className: Readonly<{
    base: string;
    focus: string;
  }>;
  format: ElementFormatType | null;
  loadingComponent?: JSX.Element | string;
  nodeKey: NodeKey;
  onError?: (error: string) => void;
  onLoad?: () => void;
  tweetID: string;
  width?: number;
  height?: number;
  isResizable: boolean;
  hideDeleteButton?: boolean;
}>;

export const TweetContainer = React.forwardRef<
  HTMLDivElement,
  {
    nodeKey: string;
    style: any;
    tweetID: string;
    width: 'inherit' | number;
    height: 'inherit' | number;
    isEmbed: boolean;
    hideDeleteButton: boolean;
  }
>((props, ref) => (
  <div
    style={{
      lineHeight: 0,
      width: '100%',
      display: 'block',
      margin: 0,
      padding: 0
    }}
  >
    <div
      ref={ref}
      style={{
        ...props.style,
        display: 'block',
        verticalAlign: 'top',
        width: '100%',
        minHeight: props.height,
        margin: 0,
        padding: 0
      }}
    />
  </div>
));

const TweetContainerWithDelete = withDeleteButton(TweetContainer);

export function TweetComponent({
  className,
  format: initialFormat,
  loadingComponent = 'Loading...', // Add default value
  nodeKey,
  onError,
  onLoad,
  tweetID,
  width: initialWidth,
  height: initialHeight,
  isResizable,
  hideDeleteButton
}: TweetComponentProps) {
  const [editor] = useLexicalComposerContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const previousTweetIDRef = useRef<string>('');
  const tweetRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isEditable] = useState(() => editor.isEditable());
  const [isTweetLoading, setIsTweetLoading] = useState(false);

  const defaultWidth = 550;
  const defaultHeight = 100;
  const containerWidth = typeof initialWidth === 'number' ? initialWidth : defaultWidth;
  const containerHeight = typeof initialHeight === 'number' ? initialHeight : defaultHeight;

  const resizeRef = useRef<{
    startWidth: number;
    startX: number;
    isResizing: boolean;
  }>({
    startWidth: 0,
    startX: 0,
    isResizing: false
  });

  const createTweet = useCallback(async () => {
    try {
      if (tweetRef.current) {
        // @ts-expect-error Twitter is attached to the window.
        await window.twttr.widgets.createTweet(tweetID, tweetRef.current, {
          width: 'max'
        });
        setIsTweetLoading(false);
        onLoad?.();
      }
    } catch (error) {
      onError?.(String(error));
    }
  }, [onError, onLoad, tweetID]);

  useEffect(() => {
    if (tweetID !== previousTweetIDRef.current) {
      setIsTweetLoading(true);

      if (isTwitterScriptLoading) {
        const script = document.createElement('script');
        script.src = WIDGET_SCRIPT_URL;
        script.async = true;
        document.body?.appendChild(script);
        script.onload = createTweet;
        if (onError) {
          script.onerror = onError as OnErrorEventHandler;
        }
      } else {
        createTweet();
      }

      previousTweetIDRef.current = tweetID;
    }
  }, [createTweet, onError, tweetID]);

  const handleResizeStart = (event: React.PointerEvent<HTMLDivElement>, isLeft: boolean) => {
    if (!isEditable || !containerRef.current) return;

    const blocker = document.createElement('div');
    blocker.style.position = 'fixed';
    blocker.style.inset = '0';
    blocker.style.cursor = 'ew-resize';
    blocker.style.userSelect = 'none';
    blocker.style.zIndex = '9999';
    document.body.appendChild(blocker);

    event.preventDefault();
    event.stopPropagation();

    const { width } = containerRef.current.getBoundingClientRect();

    resizeRef.current = {
      startWidth: width,
      startX: event.clientX,
      isResizing: true
    };
    setIsResizing(true);

    const handleMove = (moveEvent: PointerEvent) => {
      moveEvent.preventDefault();
      moveEvent.stopPropagation();

      if (!resizeRef.current.isResizing || !containerRef.current) return;

      const diff = moveEvent.clientX - resizeRef.current.startX;
      const newWidth = isLeft
        ? resizeRef.current.startWidth - diff
        : resizeRef.current.startWidth + diff;

      const minWidth = 320;
      const maxWidth = Math.min(550, editor.getRootElement()?.offsetWidth ?? 1000); // Set max to 550
      const clampedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth - 40);

      containerRef.current.style.width = `${clampedWidth}px`;
    };

    const handleUp = () => {
      if (!containerRef.current) return;

      const finalWidth = parseInt(containerRef.current.style.width, 10);

      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if ($isTweetNode(node)) {
          node.setWidthAndHeight(finalWidth, node.__height);
        }
      });

      document.body.removeChild(blocker);
      resizeRef.current.isResizing = false;
      setIsResizing(false);

      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
      document.removeEventListener('pointercancel', handleUp);
    };

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
    document.addEventListener('pointercancel', handleUp);
  };

  return (
    <BlockWithAlignableContents className={className} format="center" nodeKey={nodeKey}>
      <div
        ref={containerRef}
        className="group relative w-full rounded-2xl"
        style={{ width: containerWidth }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {isEditable && isResizable && (
          <div className="absolute inset-0" style={{ pointerEvents: 'none' }}>
            {/* Left resize handle */}
            <div
              className="absolute left-0 top-1/2 -translate-x-full -translate-y-1/2 px-2"
              style={{ pointerEvents: 'auto' }}
            >
              <div
                className={`w-1.5 h-16 rounded-full bg-[#22c55e] hover:bg-[#16a34a] transition-colors shadow-lg flex items-center justify-center cursor-ew-resize ${
                  isHovering ? 'opacity-100' : 'opacity-0'
                }`}
                onPointerDown={(e) => handleResizeStart(e, true)}
              >
                <div className="w-0.5 h-6 bg-white/50 rounded-full" />
              </div>
            </div>

            {/* Right resize handle */}
            <div
              className="absolute right-0 top-1/2 translate-x-full -translate-y-1/2 px-2"
              style={{ pointerEvents: 'auto' }}
            >
              <div
                className={`w-1.5 h-16 rounded-full bg-[#22c55e] hover:bg-[#16a34a] transition-colors shadow-lg flex items-center justify-center cursor-ew-resize ${
                  isHovering ? 'opacity-100' : 'opacity-0'
                }`}
                onPointerDown={(e) => handleResizeStart(e, false)}
              >
                <div className="w-0.5 h-6 bg-white/50 rounded-full" />
              </div>
            </div>
          </div>
        )}

        <div
          className={`relative ${isHovering && !isResizing && isEditable && !isTweetLoading ? 'pb-1 ring-2 ring-[#22c55e] rounded-xl' : ''}`}
          style={{ lineHeight: 0, width: '100%' }}
        >
          {isTweetLoading && loadingComponent}
          <TweetContainerWithDelete
            isEmbed
            tweetID={tweetID}
            style={{
              display: 'block',
              width: '100%',
              height: containerHeight
            }}
            nodeKey={nodeKey}
            ref={tweetRef}
            width={initialWidth}
            height={containerHeight}
            hideDeleteButton={hideDeleteButton}
          />
        </div>
      </div>
    </BlockWithAlignableContents>
  );
}
